/**
 * Der ENTWURF eines Layouts als unveränderliches Objekt (Layout-Editor Etappe 1). Die
 * Oberfläche kennt die Domäne nicht (Hausregel) — hier stehen nur Lesen/Setzen an einem
 * Pfad, die Umwandlung Zahl ↔ „18mm", der Vergleich und der Massstab eines Seitenbildes.
 * Die WHITELIST prüft der Server; seine Befunde (Pfade) landen über `feldFehlerKarte` am
 * Feld.
 */

export type Entwurf = Record<string, unknown>;

function istObjekt(wert: unknown): wert is Record<string, unknown> {
  return typeof wert === "object" && wert !== null && !Array.isArray(wert);
}

export function wertAn(entwurf: Entwurf, pfad: readonly string[]): unknown {
  let knoten: unknown = entwurf;
  for (const teil of pfad) {
    if (!istObjekt(knoten)) return undefined;
    knoten = knoten[teil];
  }
  return knoten;
}

/** Setzt einen Wert (bei `undefined`: entfernt ihn) — leere Objekte fallen weg, damit
 *  „Systemwert" auch im Entwurf wirklich „nichts" heisst. */
export function mitWert(entwurf: Entwurf, pfad: readonly string[], wert: unknown): Entwurf {
  const [kopf, ...rest] = pfad;
  if (kopf === undefined) return entwurf;
  const kopie: Entwurf = { ...entwurf };
  if (rest.length === 0) {
    if (wert === undefined) delete kopie[kopf];
    else kopie[kopf] = wert;
    return kopie;
  }
  const unter = istObjekt(kopie[kopf]) ? kopie[kopf] as Entwurf : {};
  const neu = mitWert(unter, rest, wert);
  if (Object.keys(neu).length === 0) delete kopie[kopf];
  else kopie[kopf] = neu;
  return kopie;
}

export function zahlAus(wert: unknown, einheit: "mm" | "pt" | "%"): string {
  return typeof wert === "string" && wert.endsWith(einheit) ? wert.slice(0, -einheit.length) : "";
}

/** Aus der Eingabe „18,5" wird „18.5mm"; leer heisst „Systemwert" (undefined). Was keine
 *  Zahl ist, geht trotzdem hinaus — der Server sagt am Feld, was erlaubt ist. */
export function mitEinheit(eingabe: string, einheit: "mm" | "pt" | "%"): string | undefined {
  const roh = eingabe.trim().replace(",", ".");
  return roh === "" ? undefined : `${roh}${einheit}`;
}

export function feldFehlerKarte(
  felder: readonly { field: string; message: string }[] | undefined,
): Record<string, string> {
  const karte: Record<string, string> = {};
  for (const f of felder ?? []) {
    if (karte[f.field] === undefined) karte[f.field] = f.message;
  }
  return karte;
}

function kanonisch(wert: unknown): string {
  if (Array.isArray(wert)) return `[${wert.map(kanonisch).join(",")}]`;
  if (istObjekt(wert)) {
    return `{${Object.keys(wert).sort()
      .map((k) => `${JSON.stringify(k)}:${kanonisch(wert[k])}`).join(",")}}`;
  }
  return JSON.stringify(wert);
}

export function istGleich(a: unknown, b: unknown): boolean {
  return kanonisch(a) === kanonisch(b);
}

/** Millimeter je Bildpunkt eines gezeichneten Seitenbildes (A4 = 210 mm breit). */
export function mmJePixel(breitePx: number): number {
  return breitePx > 0 ? 210 / breitePx : 0;
}

export function aufZehntel(mm: number): number {
  return Math.round(mm * 10) / 10;
}

/** Der Wert an einem BEFUND-Pfad des Servers — `positionsspalten[1].breite`,
 *  `seitenraender.oben` (Punkte trennen Schlüssel, `[n]` greift in Listen). */
export function wertAmPfad(entwurf: Entwurf, pfad: string): unknown {
  let knoten: unknown = entwurf;
  for (const teil of pfad.split(/\.|\[(\d+)\]/).filter((t) => t !== undefined && t !== "")) {
    if (Array.isArray(knoten) && /^\d+$/.test(teil)) knoten = knoten[Number(teil)];
    else if (istObjekt(knoten)) knoten = knoten[teil];
    else return undefined;
  }
  return knoten;
}

/** Feldfehler, deren Feld sich zwischen `alt` und `neu` geändert hat, fallen weg — ein
 *  Befund gilt dem Wert, den der Server gesehen hat, nicht der Korrektur. */
export function ohneGeaenderte(
  fehler: Record<string, string>, alt: Entwurf, neu: Entwurf,
): Record<string, string> {
  const rest: Record<string, string> = {};
  for (const [pfad, text] of Object.entries(fehler)) {
    if (istGleich(wertAmPfad(alt, pfad), wertAmPfad(neu, pfad))) rest[pfad] = text;
  }
  return rest;
}

/** Eine Spalte der Positionstabelle, wie sie im Entwurf steht (ohne Beschriftung). */
export type Spalte = {
  feld: string; titel: string; breite: string; align: "left" | "right" | "center";
  aktiv: boolean;
};

/**
 * Die Spalten, die das Register zeigt: die eigene Liste des Entwurfs (sie ersetzt im
 * Overlay die Systemliste GANZ) oder der Katalog. Fehlt einer eigenen Liste ein
 * Katalogfeld, steht es inaktiv hinten — sonst liesse es sich nie wieder einschalten.
 * Felder, die der Katalog nicht kennt, fallen weg.
 */
export function spaltenAus(
  entwurf: Entwurf, katalog: readonly (Spalte & { label: string })[],
): Spalte[] {
  const bekannt = new Map(katalog.map((k) => [k.feld, k]));
  const eigen = wertAn(entwurf, ["positionsspalten"]);
  if (!Array.isArray(eigen)) {
    return katalog.map((k) => ({ feld: k.feld, titel: k.titel, breite: k.breite,
                                 align: k.align, aktiv: k.aktiv }));
  }
  const liste: Spalte[] = [];
  for (const s of eigen as Spalte[]) {
    if (istObjekt(s) && bekannt.has(s.feld) && !liste.some((l) => l.feld === s.feld)) {
      liste.push({ ...s });
    }
  }
  for (const k of katalog) {
    if (!liste.some((l) => l.feld === k.feld)) {
      liste.push({ feld: k.feld, titel: k.titel, breite: k.breite, align: k.align,
                   aktiv: false });
    }
  }
  return liste;
}

/** Summe der aktiven Breiten in % — in Hundertsteln gerechnet wie die Whitelist. */
export function breitenSumme(spalten: readonly Spalte[]): number {
  let hundertstel = 0;
  for (const s of spalten) {
    const zahl = Number(zahlAus(s.breite, "%"));
    if (s.aktiv && zahlAus(s.breite, "%") !== "" && Number.isFinite(zahl)) {
      hundertstel += Math.round(zahl * 100);
    }
  }
  return hundertstel / 100;
}

/** Ein Eintrag von `von` nach `nach`; ausserhalb der Grenzen bleibt alles, wie es ist. */
export function verschoben<T>(liste: readonly T[], von: number, nach: number): T[] {
  const kopie = [...liste];
  if (von < 0 || von >= kopie.length || nach < 0 || nach >= kopie.length) return kopie;
  const [eintrag] = kopie.splice(von, 1);
  kopie.splice(nach, 0, eintrag as T);
  return kopie;
}

/**
 * Steht eine Grafik auf dieser Seite des Blatts? Seitenwahl wie in rechnungslayout
 * (R-MECHANIK); den Unterschriftsplatz gibt es nur auf seinen Belegarten.
 */
export function grafikAufSeite(
  g: { kind: string; pages: string; documentKinds: readonly string[] },
  seite: number, belegart: string,
): boolean {
  const seiteOk = g.pages === "ALL" || (g.pages === "FIRST" ? seite === 1 : seite > 1);
  return seiteOk && (g.kind !== "SIGNATURE" || g.documentKinds.includes(belegart));
}

/** Der Anzeigename aus dem Dateinamen: ohne Endung, ohne die Zeichen, die der Server
 *  ablehnt (R-GRAFIKNAME), höchstens 80 Zeichen; bleibt nichts, heisst sie „Grafik". */
export function grafikNameAus(dateiname: string): string {
  const name = dateiname.replace(/\.(svg|png|jpe?g)$/i, "").replace(/[<>{}"]/g, "").trim().slice(0, 80);
  return name === "" ? "Grafik" : name;
}

/** Die Reiter der Gestaltung (Spec, Oberfläche, rechts). */
export type RegisterId = "marke" | "seite" | "sektionen" | "tabelle" | "bloecke" | "texte";

/**
 * Feldpfad → Reiter (Paket „Editor-Arbeitsdialog“, ohne Entscheid): Der Server meldet seine
 * Befunde je Pfad (`seitenraender.oben`, `positionsspalten[1].titel`); der Fehlerzähler am
 * Reiter braucht sie je Reiter. Die Zuordnung hängt am ersten Pfadglied — dieselben Präfixe,
 * die die Register beim Schreiben benutzen. Ein unbekanntes Präfix zählt an keinem Reiter
 * (die Meldung oben nennt es trotzdem).
 */
export const REITER_JE_PFADPRAEFIX: Readonly<Record<string, RegisterId>> = {
  grafiken: "marke",
  seitenraender: "seite",
  sektionen: "sektionen",
  zeilenlayout: "tabelle",
  positionsspalten: "tabelle",
  bloecke: "bloecke",
  belegarten: "texte",
  texte: "texte",
};

/** Der Reiter eines Befund-Pfads (ein vorangestelltes `layout.` zählt nicht) oder `null`. */
export function reiterFuer(pfad: string): RegisterId | null {
  const praefix = pfad.replace(/^layout\./, "").split(/[.[]/)[0] ?? "";
  return Object.hasOwn(REITER_JE_PFADPRAEFIX, praefix) ? REITER_JE_PFADPRAEFIX[praefix]! : null;
}

/** Die Zahl der Feldfehler je Reiter (nur Reiter mit Fehlern stehen darin). */
export function fehlerJeReiter(fehler: Record<string, string>): Partial<Record<RegisterId, number>> {
  const zaehler: Partial<Record<RegisterId, number>> = {};
  for (const pfad of Object.keys(fehler)) {
    const reiter = reiterFuer(pfad);
    if (reiter !== null) zaehler[reiter] = (zaehler[reiter] ?? 0) + 1;
  }
  return zaehler;
}
